import * as d3 from "d3";
import { initDatePicker } from "utilities";
import { themeState } from "state/Theme";
import { i18n } from "i18n/i18n";

export type RawData<T = unknown> = {
    status: string;
    data: { ex_id: number; ex_data: T[] }[];
    exercises: [number, string][];
    student_count: number;
    deadline?: Date;
};

/**
 * Abstract base class for heatmap and line graphs.
 */
export abstract class SeriesGraph {
    // Locale settings for d3 time formatting based on i18n.
    private d3Locale = {
        dateTime: i18n.t("time.formats.default"),
        date: i18n.t("date.formats.short"),
        time: i18n.t("time.formats.short"),
        periods: [i18n.t("time.am"), i18n.t("time.pm")] as [string, string],
        days: i18n.t_a("date.day_names"),
        shortDays: i18n.t_a("date.abbr_day_names"),
        months: i18n.t_a("date.month_names").slice(1),
        shortMonths: i18n.t_a("date.abbr_month_names").slice(1),
    };

    // Observable 10 color scale, not yet present in our d3 version.
    protected readonly observable10 = ["#4269d0", "#efb118", "#ff725c", "#6cc5b0", "#3ca951", "#ff8ab7", "#a463f2", "#97bbf5", "#9c6b4e", "#9498a0"];

    // Settings and dimensions.
    protected readonly baseUrl!: string;
    protected readonly margin = { top: 20, right: 155, bottom: 40, left: 125 };
    protected readonly fontSize = 12;
    protected width: number;
    protected height: number;
    protected innerWidth: number;
    protected innerHeight: number;

    // Graph container and SVG elements.
    protected readonly selector: string;
    protected readonly container: d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>;
    protected svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, unknown>;
    protected graph: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>;

    // Data and exercise mapping.
    private seriesId: string;
    protected exerciseOrder: string[] = []; // Array of exercise IDs (as strings) in correct order.
    protected exerciseMap: Record<string, string> = {}; // Map from exercise ID to exercise name.

    // Date formatting.
    protected readonly longDateFormat = d3.timeFormat(i18n.t("date.formats.weekday_long"));

    // Time scope settings.
    private datePickerStart;
    private datePickerEnd;
    protected dateStart: string = undefined;
    protected dateEnd: string = undefined;

    /**
     * Initializes the graph container, sets up locale and theme subscriptions,
     * and optionally begins the data initialization process.
     *
     * @param {string} seriesId - The series identifier.
     * @param {string} containerId - The ID of the HTML element where the SVG is rendered.
     * @param {RawData} [data] - Optional pre-fetched data.
     */
    constructor(seriesId: string, containerId: string, data?: RawData) {
        this.seriesId = seriesId;
        this.selector = containerId;
        this.container = d3.select(this.selector);
        this.width = (this.container.node() as Element).getBoundingClientRect().width;

        d3.timeFormatDefaultLocale(this.d3Locale as d3.TimeLocaleDefinition);
        if (data) {
            this.init(true, data);
        }

        // Redraw graph when the theme changes.
        themeState.subscribe(() => {
            (this.container.node() as Element).innerHTML = "";
            this.draw(false);
        }, "computedStyle");
    }

    // Abstract method to process raw data.
    protected abstract processData(raw: RawData): void;

    /**
     * Sets the date picker limits and default dates.
     *
     * @param {Date} startDate - The default start date.
     * @param {Date} endDate - The default end date.
     */
    protected setPickerDates(startDate: Date, endDate: Date): void {
        this.datePickerStart.set("minDate", startDate);
        this.datePickerEnd.set("minDate", startDate);
        this.datePickerStart.setDate(startDate);
        this.datePickerEnd.setDate(endDate);
        this.dateStart = new Date(startDate).toISOString();
        this.dateEnd = new Date(endDate).toISOString();
    }

    /**
     * Builds and returns the URL for fetching data.
     *
     * @returns {string} The full URL string.
     */
    private getUrl(): string {
        return `/${i18n.locale()}${this.baseUrl}${this.seriesId}`;
    }

    /**
     * Draws the base SVG and graph group.
     *
     * @param {boolean} [animate=true] - Whether to animate the drawing.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected draw(_animate = true): void {
        this.innerWidth = this.width - this.margin.left - this.margin.right;
        this.innerHeight = this.height - this.margin.top - this.margin.bottom;

        this.svg = this.container
            .append("svg")
            .attr("width", this.width)
            .attr("height", this.height);

        // Redraw on window resize.
        window.onresize = () => {
            this.width = (this.container.node() as Element).getBoundingClientRect().width;
            (this.container.node() as Element).innerHTML = "";
            this.draw(false);
        };

        this.graph = this.svg
            .append("g")
            .attr("transform", `translate(${this.margin.left}, ${this.margin.top})`);
    }

    /**
     * Breaks long y-axis labels into multiple lines.
     *
     * @param {d3.Selection<d3.BaseType, unknown, SVGGElement, unknown>} selection - The y-axis label selection.
     * @param {number} width - The maximum allowed width for labels.
     * @param {Record<string, string>} exerciseMap - Map of exercise IDs to titles.
     */
    formatTitle(selection: d3.Selection<d3.BaseType, unknown, SVGGElement, unknown>, width: number, exerciseMap: Record<string, string>): void {
        selection.each((datum: string, i: number, nodeList: d3.BaseType[]) => {
            const text = d3.select(nodeList[i]);
            const words = exerciseMap[datum].split(" ").reverse();

            let word = "";
            let line: string[] = [];
            let lineNumber = 0;
            const lineHeight = 1.1; // in ems

            const y = text.attr("y");
            const dy = parseFloat(text.attr("dy"));

            let tspan = text.text(null)
                .append("tspan")
                .attr("x", 0)
                .attr("y", y)
                .attr("dy", `${dy}em`);

            while ((word = words.pop())) {
                line.push(word);
                tspan.text(line.join(" "));
                if (tspan.node().getComputedTextLength() > width) {
                    line.pop();
                    tspan.text(line.join(" "));
                    line = [word];
                    tspan = text.append("tspan")
                        .attr("text-anchor", "end")
                        .attr("x", 0)
                        .attr("y", y)
                        .attr("dy", `${++lineNumber * lineHeight + dy}em`)
                        .text(word);
                }
            }
            const fontSize = parseInt(tspan.style("font-size"));
            const tSpans = text.selectAll("tspan");
            const breaks = tSpans.size();
            tSpans.attr("y", -fontSize * (breaks - 1) / 2);
        });
    }

    /**
     * Updates the date scope based on the time pickers and reinitializes the graph.
     */
    applyScope(): void {
        this.dateStart = this.datePickerStart.selectedDates.length > 0 ?
            new Date(this.datePickerStart.selectedDates[0]).toISOString() :
            undefined;
        this.dateEnd = this.datePickerEnd.selectedDates.length > 0 ?
            new Date(this.datePickerEnd.selectedDates[0]).toISOString() :
            undefined;
        this.init();
    }

    /**
     * Determines the optimal bin step (in hours), bin boundaries, and aligned start date for a date range.
     *
     * @param {Date} minDate - The start of the date range.
     * @param {Date} maxDate - The end of the date range.
     * @param {number} targetBins - Desired number of bins.
     * @returns {[number, Array<number>, Date]} A tuple containing the bin step (hours), array of bin boundaries (in ms), and the aligned start date.
     */
    protected findBinTime(minDate: Date, maxDate: Date, targetBins: number): [number, Array<number>, Date] {
        // Predefined bin steps (in hours).
        const timeBins = [1 / 12, 1 / 6, 0.25, 0.5, 1, 4, 8, 12, 24, 48, 96, 168, 336, 672];
        const diffHours = (maxDate.getTime() - minDate.getTime()) / 3600000;
        const targetBinStep = diffHours / targetBins;

        // Find the best matching bin step.
        const bestStep = timeBins.reduce((best, current) =>
            Math.abs(current - targetBinStep) < Math.abs(best - targetBinStep) ? current : best
        );
        const binStepMs = bestStep * 3600000;

        // Align the start date.
        const alignedStart = new Date(minDate);
        if (bestStep < 1) {
            const minuteStep = bestStep * 60;
            alignedStart.setMinutes(Math.floor(minDate.getMinutes() / minuteStep) * minuteStep, 0, 0);
        } else {
            alignedStart.setMinutes(0, 0, 0);
            if (bestStep < 24) {
                alignedStart.setHours(Math.floor(minDate.getHours() / bestStep) * bestStep);
            } else {
                alignedStart.setHours(0);
                if (bestStep < 168) {
                    const dayOffset = minDate.getDate() % (bestStep / 24);
                    if (dayOffset !== 0) {
                        alignedStart.setDate(minDate.getDate() - dayOffset);
                    }
                } else {
                    // Align to Monday for weekly bins.
                    alignedStart.setDate(minDate.getDate() - (minDate.getDay() + 6) % 7);
                }
            }
        }

        // Generate bin boundaries.
        const binTicks: number[] = [alignedStart.getTime()];
        for (let time = alignedStart.getTime(); time <= maxDate.getTime(); time += binStepMs) {
            binTicks.push(time + binStepMs);
        }
        return [bestStep, binTicks, alignedStart];
    }

    /**
     * Populates internal exercise order and mapping based on provided keys.
     *
     * @param {[number, string][]} exercises - Array of [exercise ID, title] tuples.
     * @param {number[]} keys - Array of exercise IDs to filter by.
     * @returns {void}
     */
    protected parseExercises(exercises: [number, string][], keys: number[]): void {
        this.exerciseOrder = [];
        exercises.forEach(([id, title]) => {
            if (keys.includes(id)) {
                this.exerciseOrder.push("" + id);
                this.exerciseMap[id] = title;
            }
        });
    }

    /**
     * Displays a "no data" message in the graph container.
     */
    protected drawNoData(): void {
        this.showMessage(i18n.t("js.no_data"));
    }

    /**
     * Fetches data from the API with retry logic.
     *
     * @param {number} [maxAttempts=10] - Maximum number of retry attempts.
     * @param {number} [delayMs=1000] - Delay between retries in milliseconds.
     * @returns {Promise<RawData>} A promise that resolves to the fetched raw data.
     * @throws {Error} If data fetching fails after maxAttempts.
     */
    protected async fetchData(maxAttempts = 10, delayMs = 1000): Promise<RawData> {
        const url = this.getUrl();
        let attempts = 0;

        while (attempts < maxAttempts) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data: RawData = await response.json();
                if (data?.status === "not available yet") {
                    attempts++;
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                    continue;
                }

                return data;
            } catch (error) {
                if (attempts === maxAttempts - 1) {
                    throw new Error(`Failed to fetch data after ${maxAttempts} attempts: ${error.message}`);
                }
                attempts++;
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }

        throw new Error("Maximum retry attempts reached");
    }

    /**
     * Initializes and renders the series graph.
     *
     * @param {boolean} [doDraw=true] - Whether to draw the graph after initialization.
     * @param {RawData} [data] - Optional pre-fetched data.
     * @returns {Promise<void>} A promise that resolves when initialization is complete.
     */
    async init(doDraw: boolean = true, data = undefined): Promise<void> {
        try {
            this.showMessage(i18n.t("js.loading"));

            // Fetch and process data.
            const rawData: RawData = data ?? await this.fetchData();
            this.container.html(""); // Clear loading state.

            if (!rawData?.data?.length) {
                this.drawNoData();
                return;
            }

            this.processData(rawData);

            if (doDraw) {
                this.draw();
            }
        } catch (error) {
            console.error("Failed to initialize graph:", error);
            this.showMessage("Error loading graph");
        }
    }

    /**
     * Displays a message in the graph container.
     *
     * @param {string} message - The message to display.
     * @returns {void}
     */
    protected showMessage(message: string): void {
        const tempHeight = Math.max(
            this.container.node().getBoundingClientRect().height,
            100 // minimum height
        );

        this.container.html("").append("div")
            .attr("class", "graph_placeholder")
            .style("height", `${tempHeight}px`)
            .style("display", "flex")
            .style("align-items", "center")
            .text(message);
    }

    /**
     * Initializes time pickers for date range selection.
     *
     * @returns {void}
     * @throws {Error} If the date range element is not found.
     */
    protected initTimePickers(): void {
        const dateRangeElement = document.getElementById(`daterange-${this.seriesId}`);
        if (!dateRangeElement) {
            throw new Error(`Date range element not found for series ${this.seriesId}`);
        }

        dateRangeElement.hidden = false;

        const pickerOptions = {
            altFormat: i18n.t("time.formats.flatpickr_long"),
            altInput: true,
            dateFormat: i18n.t("time.formats.flatpickr_short"),
            enableTime: true,
            onClose: () => this.applyScope(),
            wrap: true,
        };

        this.datePickerStart = initDatePicker(`#scope-start-${this.seriesId}`, pickerOptions);
        this.datePickerEnd = initDatePicker(`#scope-end-${this.seriesId}`, pickerOptions);
    }
}
